import { useEffect, useRef, useState } from 'react';
import { EAN_RE } from '../../shared/catalog';
import { normalizeEan } from '../../shared/barcode';
export { normalizeEan } from '../../shared/barcode';

interface Props {
  /** Called once per can held in view, not once per decoded frame. */
  onDetected: (ean: string) => void;
  paused?: boolean;
  resetKey?: number;
  fallback?: string;
}

type Status = 'starting' | 'scanning' | 'denied' | 'unavailable';

interface Detector {
  detect(source: HTMLVideoElement): Promise<Array<{ rawValue: string; format: string }>>;
}

const FORMATS = ['ean_13', 'ean_8', 'upc_a', 'upc_e'];

/**
 * Native BarcodeDetector where it exists (Android Chrome); elsewhere (iOS
 * Safari, Firefox) the zxing-wasm ponyfill, with the .wasm served by us.
 */
async function createDetector(): Promise<Detector> {
  const native = (globalThis as { BarcodeDetector?: { new (o: object): Detector; getSupportedFormats(): Promise<string[]> } })
    .BarcodeDetector;
  if (native) {
    try {
      const supported = await native.getSupportedFormats();
      if (supported.includes('ean_13')) return new native({ formats: FORMATS.filter((f) => supported.includes(f)) });
    } catch {
      /* fall through to the ponyfill */
    }
  }
  const [{ BarcodeDetector, prepareZXingModule }, { default: wasmUrl }] = await Promise.all([
    import('barcode-detector/ponyfill'),
    import('zxing-wasm/reader/zxing_reader.wasm?url'),
  ]);
  prepareZXingModule({
    overrides: {
      locateFile: (path: string, prefix: string) => (path.endsWith('.wasm') ? wasmUrl : prefix + path),
    },
  });
  return new BarcodeDetector({ formats: FORMATS as never });
}

/** How long a code has to be out of view before the same one counts as a new scan. */
const REPEAT_MS = 1500;

/** The live camera view, inline on the page; it keeps scanning after a hit. */
export function Scanner({
  onDetected,
  paused = false,
  resetKey = 0,
  fallback = 'Zoek hieronder op naam of op de code onder de streepjes.',
}: Props) {
  const video = useRef<HTMLVideoElement>(null);
  const [status, setStatus] = useState<Status>('starting');
  const onDetectedRef = useRef(onDetected);
  onDetectedRef.current = onDetected;
  const pausedRef = useRef(paused);
  const frameEpoch = useRef(0);
  if (pausedRef.current !== paused) frameEpoch.current++;
  pausedRef.current = paused;
  const resetRef = useRef(resetKey);
  if (resetRef.current !== resetKey) frameEpoch.current++;
  resetRef.current = resetKey;

  useEffect(() => {
    let stream: MediaStream | null = null;
    let timer = 0;
    let cancelled = false;
    let lastCode = '';
    let lastSeen = 0;
    let previousReset = resetRef.current;

    const stop = () => {
      stream?.getTracks().forEach((t) => t.stop());
      stream = null;
    };

    const start = async () => {
      if (!navigator.mediaDevices?.getUserMedia) {
        setStatus('unavailable');
        return;
      }
      let granted: MediaStream;
      try {
        granted = await navigator.mediaDevices.getUserMedia({
          video: { facingMode: { ideal: 'environment' }, width: { ideal: 1280 }, height: { ideal: 720 } },
          audio: false,
        });
      } catch (err) {
        if (cancelled) return;
        const name = (err as DOMException)?.name;
        setStatus(name === 'NotAllowedError' || name === 'SecurityError' ? 'denied' : 'unavailable');
        return;
      }
      // Permission can be answered after the page was left: release the camera right away.
      if (cancelled || !video.current) return granted.getTracks().forEach((t) => t.stop());
      stream = granted;
      video.current.srcObject = stream;
      await video.current.play().catch(() => {});
      if (cancelled) return;
      setStatus('scanning');

      let detector: Detector;
      try {
        detector = await createDetector();
      } catch {
        if (cancelled) return;
        stop();
        setStatus('unavailable');
        return;
      }

      const tick = async () => {
        if (cancelled) return;
        const v = video.current;
        // Nothing is read behind an open sheet: the can is still in front of the lens there.
        if (previousReset !== resetRef.current) {
          lastCode = '';
          previousReset = resetRef.current;
        }
        const blocked = () => pausedRef.current || !!document.querySelector('dialog[open]');
        if (blocked()) lastSeen = Date.now();
        if (v && v.readyState >= 2 && !blocked()) {
          try {
            const epoch = frameEpoch.current;
            const found = await detector.detect(v);
            if (cancelled) return;
            if (blocked() || frameEpoch.current !== epoch) {
              timer = window.setTimeout(tick, 120);
              return;
            }
            const code = found.map((b) => normalizeEan(b.rawValue)).find((c) => EAN_RE.test(c));
            if (code) {
              const now = Date.now();
              const repeat = code === lastCode && now - lastSeen < REPEAT_MS;
              lastCode = code;
              lastSeen = now;
              if (!repeat) {
                navigator.vibrate?.(40);
                onDetectedRef.current(code);
              }
            }
          } catch {
            /* a frame that fails to decode is just the next frame */
          }
        }
        timer = window.setTimeout(tick, 120);
      };
      void tick();
    };

    void start();
    return () => {
      cancelled = true;
      window.clearTimeout(timer);
      stop();
    };
  }, []);

  const off = status === 'denied' || status === 'unavailable';

  return (
    <section className={`scanner${off ? ' scanner--off' : ''}`} aria-label="Streepjescode scannen">
      <video ref={video} className="scanner__video" playsInline muted aria-hidden="true" />
      {!off && (
        <>
          <div className="scanner__finder">{status === 'scanning' && <div className="scanner__line" />}</div>
          <div className="scanner__hint">
            {status === 'scanning' ? 'Richt op de streepjescode onderop het blik' : 'Camera starten…'}
          </div>
        </>
      )}
      {status === 'denied' && (
        <div className="scanner__message" role="status">
          <strong>Geen camera</strong>
          Je hebt de camera niet toegestaan. Zet hem aan in de instellingen van je browser (bij de site of via het
          slotje). {fallback}
        </div>
      )}
      {status === 'unavailable' && (
        <div className="scanner__message" role="status">
          <strong>Camera niet beschikbaar</strong>
          Dit apparaat of deze browser geeft geen camerabeeld. {fallback}
        </div>
      )}
    </section>
  );
}
